import React, { ComponentProps } from 'react';
import cx from 'classnames';

import './page-layout.scss';
import AkColors from '@styles/legos/colors.module.scss';
const {
    akBlue,
    akBlack,
    akChiffon,
    akClearDay,
    akCloud,
    akCotton,
    akFrost,
    akGreen,
    akIron,
    akIvory,
    akMintCream,
    akNewsBanner,
    akOrange,
    akPaper,
    akRed,
    akTeal,
    akYellow,
    akVapor,
    akWhite,
} = AkColors;

export const supportedColors = {
    inherit: 'inherit',
    akBlue,
    akBlack,
    akChiffon,
    akClearDay,
    akCloud,
    akCotton,
    akFrost,
    akGreen,
    akIron,
    akIvory,
    akMintCream,
    akNewsBanner,
    akOrange,
    akPaper,
    akRed,
    akTeal,
    akYellow,
    akVapor,
    akWhite,
} as const;

type BaseClass =
    | 'page-layout'
    | 'page-layout__with-subgrid'
    | 'page-layout-full'
    | 'page-layout-full__with-subgrid'
    | 'page-layout-app'
    | 'page-layout-app__with-subgrid'
    | 'page-layout-app-content'
    | 'page-layout-app-content__with-subgrid';
type LayoutComponentProps = {
    baseClass: BaseClass;
    bgColor: Colors;
} & Props;
function LayoutComponent(props: LayoutComponentProps) {
    const { children, className, baseClass, style, bgColor } = props;

    const newStyle = { ...style };
    if (!style?.backgroundImage) newStyle['background'] = supportedColors[bgColor];

    return (
        <div className={cx(baseClass, className)} style={newStyle}>
            {children}
        </div>
    );
}

type Colors = keyof typeof supportedColors;
type Props = {
    className?: string;
    subgrid?: boolean;
} & ComponentProps<'div'>;
type LayoutProps = {
    bgColor?: Colors;
} & Props;
export function PageLayout(props: LayoutProps) {
    return (
        <LayoutComponent baseClass='page-layout' style={props.style} bgColor='inherit' {...props} />
    );
}

export function PageLayoutTesting() {
    return (
        <>
            <div>Patente #1.1</div>
            <PageLayoutFull>
                <div style={{ border: '1px solid red' }}>Patente #1.2</div>
            </PageLayoutFull>
            <div>Patente #2.1</div>
            <PageLayoutApp>
                <div style={{ border: '1px solid red' }}>Patente #2.2</div>
            </PageLayoutApp>
            <div>Patente #3.1</div>
            <PageLayoutAppContent>
                <div style={{ border: '1px solid red' }}>Patente #3.2</div>
            </PageLayoutAppContent>

            <div>SPLITTER</div>

            <div>Patente #1.1</div>
            <PageLayoutFull style={{ border: '1px solid blue' }}>
                <PageLayoutContent>
                    <div style={{ border: '1px solid red' }}>Patente #1.2</div>
                </PageLayoutContent>
            </PageLayoutFull>
            <div>Patente #2.1</div>
            <PageLayoutFull style={{ border: '1px solid yellow' }}>
                <PageLayoutApp>
                    <div style={{ border: '1px solid red' }}>Patente #2.2</div>
                </PageLayoutApp>
            </PageLayoutFull>
            <div>Patente #3.1</div>
            <PageLayoutFull style={{ border: '1px solid black' }}>
                <PageLayoutAppContent>
                    <div style={{ border: '1px solid red' }}>Patente #3.2</div>
                </PageLayoutAppContent>
            </PageLayoutFull>
        </>
    );
}

/*************************************************************************/
/*                                                                       */
/*     Assumes that all these components have PageLayout as a parent     */
/*                                                                       */
/*************************************************************************/
const divStyle = {};
export function PageLayoutFull(props: LayoutProps) {
    const { subgrid = false, bgColor = 'inherit', className } = props;
    const layoutClassName = 'page-layout-full';

    return (
        <LayoutComponent
            baseClass={
                cx(
                    layoutClassName,
                    className,
                    subgrid ? `${layoutClassName}__with-subgrid` : ''
                ) as BaseClass
            }
            style={{ ...divStyle, ...props.style }}
            bgColor={bgColor}
            {...props}
        />
    );
}

export function PageLayoutApp(props: LayoutProps) {
    const { subgrid = false, bgColor = 'inherit', className } = props;
    const layoutClassName = 'page-layout-app';
    return (
        <LayoutComponent
            baseClass={
                cx(
                    layoutClassName,
                    className,
                    subgrid ? `${layoutClassName}__with-subgrid` : ''
                ) as BaseClass
            }
            style={{ ...divStyle, ...props.style }}
            bgColor={bgColor}
            {...props}
        />
    );
}

export function PageLayoutContent(props: LayoutProps) {
    const { subgrid = false, bgColor = 'inherit', className } = props;
    const layoutClassName = 'page-layout-content';
    return (
        <LayoutComponent
            baseClass={
                cx(
                    layoutClassName,
                    className,
                    subgrid ? `${layoutClassName}__with-subgrid` : ''
                ) as BaseClass
            }
            style={{ ...divStyle, ...props.style }}
            bgColor={bgColor}
            {...props}
        />
    );
}

export function PageLayoutAppContent(props: LayoutProps) {
    const { subgrid = false, bgColor = 'inherit', className } = props;
    const layoutClassName = 'page-layout-app-content';
    return (
        <LayoutComponent
            baseClass={
                cx(
                    layoutClassName,
                    className,
                    subgrid ? `${layoutClassName}__with-subgrid` : ''
                ) as BaseClass
            }
            style={{ ...divStyle, ...props.style }}
            bgColor={bgColor}
            {...props}
        />
    );
}

export function PageLayoutFullContent(props: LayoutProps) {
    return (
        <PageLayout>
            <PageLayoutFull>
                <PageLayoutAppContent subgrid {...props} />
            </PageLayoutFull>
        </PageLayout>
    );
}
